// Low-level WebRTC helpers for the MEETING module.
// The orchestration (call lifecycle, socket signaling) lives in meeting.js;
// this file only knows about media + the RTCPeerConnection itself.

import axios from '@/axios.js'

const ICE_FALLBACK = [{ urls: 'stun:stun.l.google.com:19302' }]

// TURN credentials are short-lived (api/app/controllers/meeting.controller.js):
// cache them for 80 % of their lifetime only, then fetch fresh ones. A tab
// left open for hours used to reuse an expired credential → relay refused →
// peers behind strict NATs never connected.
let iceCache = null // { iceServers, expiresAt }

export async function getIceServers (force) {
  if (!force && iceCache && Date.now() < iceCache.expiresAt) return iceCache.iceServers
  try {
    const { data } = await axios.get('/meeting/ice-config')
    if (data && Array.isArray(data.iceServers) && data.iceServers.length) {
      const ttl = Math.min(Number(data.ttl) || 3600, 3600)
      iceCache = { iceServers: data.iceServers, expiresAt: Date.now() + (ttl * 800) }
      return data.iceServers
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[meeting] ice-config failed, using public STUN only', e && e.message)
  }
  return ICE_FALLBACK
}

// Devices chosen in "Test your microphone & camera" (DeviceCheck.vue), kept
// per browser: { audioIn, videoIn, audioOut } deviceIds. A missing or
// unplugged device simply falls back to the browser default ("ideal").
const DEVICES_KEY = 'meeting-devices'

export function loadDevicePrefs () {
  try { return JSON.parse(localStorage.getItem(DEVICES_KEY)) || {} } catch (e) { return {} }
}

export function saveDevicePrefs (prefs) {
  try { localStorage.setItem(DEVICES_KEY, JSON.stringify(prefs || {})) } catch (e) { /* private mode */ }
}

export async function listDevices () {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return { audioIn: [], videoIn: [], audioOut: [] }
  const all = await navigator.mediaDevices.enumerateDevices()
  const pick = (kind) => all.filter(d => d.kind === kind).map((d, i) => ({
    deviceId: d.deviceId,
    label: d.label || `${kind === 'videoinput' ? 'Camera' : kind === 'audioinput' ? 'Microphone' : 'Speaker'} ${i + 1}`
  }))
  return { audioIn: pick('audioinput'), videoIn: pick('videoinput'), audioOut: pick('audiooutput') }
}

// Camera + mic, falling back to mic only when the camera is missing, busy or
// refused: an audio-only call beats no call at all (desktop PCs without a
// webcam are common in the office).
export async function getLocalMedia (prefs) {
  const p = prefs || loadDevicePrefs()
  const audio = { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
  if (p.audioIn) audio.deviceId = { ideal: p.audioIn }
  const video = { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24, max: 30 } }
  if (p.videoIn) video.deviceId = { ideal: p.videoIn }
  try {
    return await navigator.mediaDevices.getUserMedia({ audio, video })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[meeting] camera unavailable, trying audio only:', e && e.name)
    return navigator.mediaDevices.getUserMedia({ audio, video: false })
  }
}

export async function getScreenMedia () {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: { ideal: 15, max: 30 } },
    audio: false
  })
  const track = stream.getVideoTracks()[0]
  // "detail" = favour sharpness over motion (text stays readable).
  if (track && 'contentHint' in track) track.contentHint = 'detail'
  return stream
}

// Create a peer connection wired to caller-supplied callbacks. The caller owns
// adding the local tracks and creating offers/answers.
export function createPeerConnection ({ iceServers, onIceCandidate, onTrack, onStateChange }) {
  const pc = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' })

  pc.onicecandidate = (event) => {
    if (event.candidate) onIceCandidate(event.candidate)
  }
  pc.ontrack = (event) => {
    onTrack(event.track, event.streams && event.streams[0])
  }
  pc.onconnectionstatechange = () => {
    if (onStateChange) onStateChange(pc.connectionState)
  }

  return pc
}

// The sender of the (single) outgoing video / audio slot, with or without a track.
function senderOfKind (pc, kind) {
  const t = pc.getTransceivers().find(x => x.receiver && x.receiver.track && x.receiver.track.kind === kind)
  return t ? t.sender : null
}
export function videoSender (pc) { return senderOfKind(pc, 'video') }
export function audioSender (pc) { return senderOfKind(pc, 'audio') }

// Cap the outgoing video bitrate (full mesh = one encoder per peer, so keep
// each one modest) and tell the encoder what to sacrifice when the link gets
// tight: resolution for a camera, frame rate for a shared screen.
export async function applyVideoParams (sender, { maxBitrate, screen }) {
  if (!sender || typeof sender.getParameters !== 'function') return
  try {
    const params = sender.getParameters()
    if (!params.encodings || !params.encodings.length) return // not negotiated yet
    params.encodings[0].maxBitrate = maxBitrate
    params.degradationPreference = screen ? 'maintain-resolution' : 'balanced'
    await sender.setParameters(params)
  } catch (e) { /* older browsers: best effort */ }
}

export function stopStream (stream) {
  if (!stream) return
  stream.getTracks().forEach((t) => t.stop())
}
